import React from 'react';
import { x } from '@xstyled/styled-components';
import { t } from 'ttag';
import DOMPurify from 'dompurify';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { Flex } from '~/ui/flex/Flex';
import { Button } from '~/ui/buttons/Button';
import { useTrainingUrl } from '~/training/useTrainingUrl';

import { ThemeAwareLogo } from '~/ui/logo/Logo';
import { useTrainingStore } from '../state/useTrainingStore';
import { TrainingState } from '../state/TrainingState';
import { select } from '~/lib/select';
import { Overlay } from '~/ui/overlay/Overlay';
import { formatDurationInMinutes } from '~/lib/formatters/duration';

import useNavigateWithoutFullPageReload from '~/lib/navigations/hooks/useNavigateWithoutFullPageReload';

const selector = (s: TrainingState) =>
  select(
    s,
    'duration',
    'remainingDurationInMinutesForCourseTimers',
    'shouldDisplayTimerStillRunningModal',
    'setShouldDisplayTimeRemainingPill',
    'hideTimerStillRunningModal',
    'getFirstChapterFirstSequenceId',
    'regionName'
  );

export const TimerStillRunningModal: React.FC = () => {
  const url = useTrainingUrl();
  const {
    duration,
    remainingDurationInMinutesForCourseTimers: remainingDurationInMinutes,
    shouldDisplayTimerStillRunningModal,
    setShouldDisplayTimeRemainingPill,
    hideTimerStillRunningModal,
    getFirstChapterFirstSequenceId,
    regionName: unsanitizedRegionName
  } = useTrainingStore(selector);
  const navigateWithoutFullPageReload = useNavigateWithoutFullPageReload();

  const regionName = DOMPurify.sanitize(unsanitizedRegionName || '', {
    USE_PROFILES: { html: true }
  });
  const formattedDuration = formatDurationInMinutes(duration);

  const handleReviewCourseClick = () => {
    hideTimerStillRunningModal();

    if (!url) return;

    const firstSequenceId = getFirstChapterFirstSequenceId();

    if (!firstSequenceId) return;

    navigateWithoutFullPageReload(`${url}/${firstSequenceId}`);

    setShouldDisplayTimeRemainingPill(true);
  };

  if (!shouldDisplayTimerStillRunningModal) return null;

  return (
    <Overlay open opacity={0.5} dataTestId="still-pending-modal-overlay">
      <LlamaModal
        horizontal="center"
        vertical="center"
        close={hideTimerStillRunningModal}
      >
        <Flex
          data-test-id="timer-still-pending-modal"
          w="md"
          minHeight="md"
          flexDirection="column"
          p="xl"
          alignItems="center"
          justifyContent="space-between"
          minWidth={{ sm: '100%', lg: '400px' }}
        >
          <ThemeAwareLogo height={36} title="EasyLlama" />

          <x.h2 mb="lg" mt="lg" textAlign="center">
            {t`Almost There — The Timer's Still Running`}
          </x.h2>

          <x.p
            mb="lg"
            textAlign="center"
            dangerouslySetInnerHTML={{
              __html: t`<b>${regionName}</b> law requires at least <b>${formattedDuration}</b> of interactive training. You've completed the main content — please continue reviewing materials until the timer reaches 0:00. The timer pauses after 2 minutes of inactivity.`
            }}
          />

          <x.p mb="lg" textAlign="center">
            <x.span fontWeight="bold">{t`Time left: ${remainingDurationInMinutes}m`}</x.span>
          </x.p>

          <Button
            onClick={handleReviewCourseClick}
            data-test-id="review-course-button"
          >
            {t`Review the course`}
          </Button>
        </Flex>
      </LlamaModal>
    </Overlay>
  );
};
